/**
 * Their operative legend, as a feed: every mission they have filed, newest
 * first, one post each — the lock it opened (its seal and word), when, the
 * mission's name, what they sent, what they did and how it felt. × or a tap
 * on the dark around it closes it.
 */
import { useEffect } from "react";
import { X } from "lucide-react";
import { LOCK_WORDS } from "./vault-clips";
import type { FiledMission } from "@/features/funnel/api/use-mission-file";
import braveSeal from "./lock-seals/brave.webp";
import reliableSeal from "./lock-seals/reliable.webp";
import aimSeal from "./lock-seals/aim.webp";
import connectSeal from "./lock-seals/connect.webp";
import influenceSeal from "./lock-seals/influence.webp";
import championSeal from "./lock-seals/champion.webp";
import outlastSeal from "./lock-seals/outlast.webp";

/** The door's seven locks, in order: mission n opens lock n. */
const SEALS = [
  braveSeal,
  reliableSeal,
  aimSeal,
  connectSeal,
  influenceSeal,
  championSeal,
  outlastSeal,
];
const LOCKS = LOCK_WORDS.map((word, i) => ({ word, seal: SEALS[i] }));

const LABEL =
  "font-mono text-[10px] tracking-[.22em] text-[#d6a94e]/80 uppercase";

export function LegendCarousel({
  missions,
  onRetry,
  onClose,
}: {
  /** newest first, as the file comes; null until it is read */
  missions: FiledMission[] | null;
  /** the file failed to load: offer to try again */
  onRetry?: () => void;
  onClose: () => void;
}) {
  const total = missions?.length ?? 0;
  // Escape closes it, as × does
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    // `backwards`, not `both`: a style left on it by the finished fade stops
    // iPhone Safari swipe-scrolling the feed inside (see laptop-desktop.tsx)
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Operative legend"
      onClick={onClose}
      className="fixed inset-0 z-[50] flex animate-[smFadeIn_.3s_ease_backwards] flex-col bg-[#070605] font-sans text-[#f4eddb]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex shrink-0 items-center justify-between border-b border-[#d6a94e]/20 px-5 py-4"
      >
        <div>
          <div className="font-mono text-[10.5px] tracking-[.3em] text-[#d6a94e] uppercase">
            ▸ Operative legend
          </div>
          <div className="mt-0.5 text-[13px] text-[#a99d86]">
            {missions ? (
              <>
                {Math.min(total, LOCKS.length)} of {LOCKS.length} locks open ·{" "}
                {total} mission{total === 1 ? "" : "s"} filed
              </>
            ) : onRetry ? (
              <>
                Couldn't load your file —{" "}
                <button
                  type="button"
                  onClick={onRetry}
                  className="cursor-pointer text-[#d6a94e] underline underline-offset-2"
                >
                  Retry
                </button>
              </>
            ) : (
              "—"
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="grid size-10 cursor-pointer place-items-center rounded-full border border-[#d6a94e]/40 bg-black/50"
        >
          <X className="size-5" />
        </button>
      </div>

      <div
        onClick={(e) => e.stopPropagation()}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
      >
        {/* unread: no feed, rather than seven locks all shut */}
        {missions ? (
          <div className="mx-auto flex max-w-[520px] flex-col gap-3 px-4 py-4">
            {missions.map((m, i) => {
              // newest first: the oldest filed opened lock 1
              const n = total - i;
              const lock = LOCKS[n - 1];
              return (
                <article
                  key={m.glowId}
                  className="rounded-[14px] border border-white/10 bg-[#14110d] px-4 pt-3.5 pb-4"
                >
                  <header className="flex items-center gap-3">
                    {lock ? (
                      <img src={lock.seal} alt="" className="size-9" />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[10.5px] tracking-[.2em] text-[#d6a94e] uppercase">
                        {lock
                          ? `Proved ${lock.word} · Lock ${n}`
                          : `Mission ${n}`}
                      </div>
                      {m.filedAt ? (
                        <div className="text-[12px] text-[#8c8070]">
                          {new Date(m.filedAt).toLocaleDateString(undefined, {
                            month: "long",
                            day: "numeric",
                          })}
                        </div>
                      ) : null}
                    </div>
                  </header>
                  <h3 className="mt-2.5 text-[18px] leading-tight font-extrabold">
                    {m.name}
                  </h3>
                  {m.mediaUrl ? (
                    m.format === "video" ? (
                      <video
                        src={m.mediaUrl}
                        controls
                        playsInline
                        className="mt-3 max-h-[60vh] w-full rounded-[10px] bg-black object-contain"
                      />
                    ) : (
                      <img
                        src={m.mediaUrl}
                        alt=""
                        className="mt-3 max-h-[60vh] w-full rounded-[10px] bg-black object-cover"
                      />
                    )
                  ) : null}
                  {m.whatIDid ? (
                    <>
                      <div className={`mt-3 ${LABEL}`}>What I did</div>
                      <p className="mt-0.5 text-[15px] leading-snug">
                        {m.whatIDid}
                      </p>
                    </>
                  ) : null}
                  {m.howItFelt ? (
                    <>
                      <div className={`mt-2.5 ${LABEL}`}>How it felt</div>
                      <p className="mt-0.5 text-[15px] leading-snug text-[#d8ccb0]">
                        {m.howItFelt}
                      </p>
                    </>
                  ) : null}
                </article>
              );
            })}
            {/* the locks still shut, their truth kept secret */}
            {total < LOCKS.length ? (
              <div className={`mt-2 px-1 ${LABEL}`}>Still locked</div>
            ) : null}
            {LOCKS.slice(total).map((lock, j) => {
              const n = total + j + 1;
              return (
                <article
                  key={lock.word}
                  className="flex items-center gap-3 rounded-[14px] border border-dashed border-white/10 bg-[#0e0c09] px-4 py-3.5"
                >
                  <img
                    src={lock.seal}
                    alt=""
                    className="size-9 opacity-25 blur-[1px] grayscale"
                  />
                  <div>
                    <div className="font-mono text-[10.5px] tracking-[.2em] text-[#8c8070] uppercase">
                      Proved ????? · Lock {n}
                    </div>
                    <div className="text-[13px] text-[#6f6656]">
                      Complete mission {n} to unlock
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </div>

      <style>{`@keyframes smFadeIn{from{opacity:0}to{opacity:1}}`}</style>
    </div>
  );
}
